import { apiClient } from './client';
import type { AppRole } from '@/types';

/** A team reference shown on a user row. */
export interface ManagedUserTeam {
  teamId: number;
  name: string;
}

/**
 * Row model for the User Management page.
 *
 * `userId`, `email`, `name`, `teams` come from the local database. Everything
 * else is owned by Auth0 and merged in server-side by the API (the browser
 * never calls the Auth0 Management API). When Auth0 could not be reached the
 * Auth0-owned fields are `undefined` and `UsersListResult.auth0Available` is
 * `false`.
 */
export interface ManagedUser {
  userId: number;
  email: string;
  /** Display name, combined from the backend's `firstName` / `lastName`. */
  name?: string;
  /** Auth0 `sub` (e.g. `auth0|…`, `okta-iris-northamerica|…`). */
  auth0Id?: string;
  /** Connection prefix parsed from `auth0Id` (e.g. `auth0`, `okta-iris-northamerica`). */
  connection?: string;
  /** Highest-ranked application role held in Auth0; `null` = no role assigned. */
  role?: AppRole | null;
  emailVerified?: boolean;
  picture?: string;
  isBlocked?: boolean;
  lastLogin?: string;
  /** True when the local row is linked to an Auth0 user that no longer exists. */
  missingInAuth0?: boolean;
  teams: ManagedUserTeam[];
}

/** Result of a user listing. */
export interface UsersListResult {
  users: ManagedUser[];
  /** False when the API could not reach Auth0 — role/status columns are unknown. */
  auth0Available: boolean;
  /** Short reason when `auth0Available` is false (e.g. "Auth0 Management API returned HTTP 403."). */
  auth0Error?: string;
}

/** Raw row returned by the API. */
interface UserListItemDto {
  userId: number;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  auth0Id?: string | null;
  connection?: string | null;
  role?: string | null;
  roles?: string[] | null;
  emailVerified?: boolean | null;
  picture?: string | null;
  isBlocked?: boolean | null;
  lastLogin?: string | null;
  missingInAuth0?: boolean | null;
  teams?: { teamId: number; name: string }[] | null;
}

/** Envelope returned by `GET /admin/users` and `GET /my/users`. */
interface UserListResponseDto {
  users: UserListItemDto[];
  auth0Available: boolean;
  auth0Error?: string | null;
}

export type ManagedUserScope = 'all' | 'mine';

const base = (scope: ManagedUserScope) => (scope === 'all' ? '/admin/users' : '/my/users');

/**
 * User Management API. Every call goes through our backend, which proxies the
 * Auth0 Management API and enforces the role-rank rules
 * (see backend docs/authorization-and-roles.md).
 */
export const usersApi = {
  /** All users (crm:admin) or users in the caller's teams (crm:editor+). */
  list: async (scope: ManagedUserScope): Promise<UsersListResult> => {
    const { data } = await apiClient.get<UserListResponseDto>(base(scope));
    return toListResult(data);
  },

  /** Single user within the given scope. */
  get: async (scope: ManagedUserScope, userId: number): Promise<ManagedUser> => {
    const { data } = await apiClient.get<UserListItemDto>(`${base(scope)}/${userId}`);
    return toManagedUser(data);
  },

  /**
   * Replace the user's application role in Auth0. Admins may set any role
   * below Admin; editors only roles below their own rank, on users below their
   * own rank, within their teams. `crm:admin` is never accepted.
   */
  updateRole: async (scope: ManagedUserScope, userId: number, role: AppRole): Promise<ManagedUser> => {
    const { data } = await apiClient.put<UserListItemDto>(`${base(scope)}/${userId}/role`, { role });
    return toManagedUser(data);
  },

  /** Block / unblock login in Auth0 (crm:admin only). */
  updateStatus: async (userId: number, isBlocked: boolean): Promise<ManagedUser> => {
    const { data } = await apiClient.put<UserListItemDto>(`/admin/users/${userId}/status`, {
      isBlocked,
    });
    return toManagedUser(data);
  },

  /** Replace the user's full set of team memberships (crm:admin only). */
  updateTeams: async (userId: number, teamIds: number[]): Promise<ManagedUser> => {
    const { data } = await apiClient.put<UserListItemDto>(`/admin/users/${userId}/teams`, {
      teamIds,
    });
    return toManagedUser(data);
  },
};

function toListResult(dto: UserListResponseDto): UsersListResult {
  return {
    users: (dto.users ?? []).map(toManagedUser),
    auth0Available: dto.auth0Available,
    auth0Error: dto.auth0Error ?? undefined,
  };
}

function toManagedUser(dto: UserListItemDto): ManagedUser {
  return {
    userId: dto.userId,
    email: dto.email,
    name: fullName(dto.firstName, dto.lastName),
    auth0Id: dto.auth0Id ?? undefined,
    connection: dto.connection ?? undefined,
    // `roles` present (even empty) means Auth0 answered; null role then means "no role".
    role: dto.roles == null ? undefined : ((dto.role as AppRole | null | undefined) ?? null),
    emailVerified: dto.emailVerified ?? undefined,
    picture: dto.picture ?? undefined,
    isBlocked: dto.isBlocked ?? undefined,
    lastLogin: dto.lastLogin ?? undefined,
    missingInAuth0: dto.missingInAuth0 ?? undefined,
    teams: dto.teams ?? [],
  };
}

/** Combine first/last name into a display name; `undefined` when both are blank. */
export function fullName(first?: string | null, last?: string | null): string | undefined {
  const name = [first, last]
    .map((part) => part?.trim() ?? '')
    .filter(Boolean)
    .join(' ');
  return name || undefined;
}
